import { CommonModule } from '@angular/common'
import { Component, DestroyRef, OnInit, computed, effect, inject, input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'
import { MessageService } from '@openng/optimus-ui/api'
import { OptimUiSharedModule } from '../../../shared/optimus-ui-shared.module'
import { ClassManagerApiService } from '../../../_services/system_api/classManager/api.service'
import {
  SiteTimelineApiService,
  SiteTimelineEvent,
  SiteTimelineEventStatus,
  SiteTimelineSite
} from '../../../_services'
import { stripHtml } from '../../../_helper/html-format.util'
import { warningSourceLabel, warningSourceLabels } from '../../../_helper/data-warning.util'

interface SiteOption {
  label: string
  value: string
}

interface TimelineGroup {
  key: string
  label: string
  events: SiteTimelineEvent[]
}

@Component({
  selector: 'app-site-timeline',
  standalone: true,
  imports: [OptimUiSharedModule, CommonModule, FormsModule, RouterLink],
  templateUrl: './site-timeline.html',
  styleUrls: ['./site-timeline.scss']
})
export class SiteTimelineComponent implements OnInit {
  private readonly api = inject(SiteTimelineApiService)
  private readonly classApi = inject(ClassManagerApiService)
  private readonly messageService = inject(MessageService)
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)
  private loadToken = 0
  private loadedFraz = ''

  /** Frazionario passato dal contenitore (site hub); se assente usa la query ?fraz=. */
  readonly frazInput = input<string | null>(null)
  /** Nasconde testata, selettore e riepilogo quando incorporato in un'altra pagina. */
  readonly embedded = input(false)
  /** Mostra la checkbox RTU: quando attiva gli eventi RTU sono nascosti di default. */
  readonly rtuFilter = input(false)

  readonly sites = signal<SiteOption[]>([])
  readonly fraz = signal('')
  readonly site = signal<SiteTimelineSite | null>(null)
  readonly events = signal<SiteTimelineEvent[]>([])
  readonly warnings = signal<{ source: string, message: string }[]>([])
  readonly loading = signal(false)
  readonly error = signal<string | null>(null)
  readonly mode = signal<'all' | 'operational'>('all')
  readonly area = signal('')
  /** Eventi RTU visibili: spento di default quando `rtuFilter` e' attivo. */
  readonly showRtu = signal(false)
  readonly visible = signal(40)
  readonly detailVisible = signal(false)
  readonly detailEvent = signal<SiteTimelineEvent | null>(null)
  readonly detailRow = signal<Record<string, any> | null>(null)
  readonly detailTable = signal<string | null>(null)
  readonly detailLoading = signal(false)
  readonly detailError = signal<string | null>(null)
  private detailRequestToken = 0

  readonly areas = [
    { label: 'Tutte le aree', value: '' },
    { label: 'SDWAN Fortinet', value: 'fortinet' },
    { label: 'SDWAN Cisco', value: 'cisco' },
    { label: 'RTU', value: 'rtu' },
    { label: 'Speedtest', value: 'speedtest' },
    { label: 'NRD', value: 'nrd' },
    { label: 'VOIP', value: 'voip' },
    { label: 'Workflow', value: 'workflow' },
    { label: 'Pianificazione', value: 'pianificazione' },
    { label: 'Mediation', value: 'mediation' },
    { label: 'Support', value: 'support' },
    { label: 'Task queue', value: 'queue' }
  ]

  readonly filtered = computed(() => {
    const area = this.area()
    const mode = this.mode()
    // Con il filtro RTU attivo gli eventi RTU restano nascosti finche' non
    // l'utente li abilita o seleziona esplicitamente l'area RTU.
    const hideRtu = this.rtuFilter() && !this.showRtu() && area !== 'rtu'
    return this.events().filter((event) => {
      if (mode === 'operational' && event.source === 'queue') {
        return false
      }
      if (hideRtu && event.area === 'rtu') {
        return false
      }
      if (area && event.area !== area) {
        return false
      }
      return true
    })
  })

  /** Eventi RTU presenti, mostrati accanto alla checkbox come contatore. */
  readonly rtuCount = computed(() => this.events().filter((event) => event.area === 'rtu').length)

  readonly grouped = computed<TimelineGroup[]>(() => {
    const list = this.filtered().slice(0, this.visible())
    const groups: TimelineGroup[] = []
    list.forEach((event) => {
      const key = event.date.slice(0, 10)
      let group = groups[groups.length - 1]
      if (!group || group.key !== key) {
        group = { key, label: this.formatDay(event.date), events: [] }
        groups.push(group)
      }
      group.events.push(event)
    })
    return groups
  })

  readonly hasMore = computed(() => this.filtered().length > this.visible())

  /** Fonti che non hanno risposto, in forma leggibile (banner warning). */
  readonly warningLabels = computed(() => warningSourceLabels(this.warnings()))

  /** Dettaglio del warning: fonte leggibile + messaggio del backend. */
  readonly warningDetails = computed(() => this.warnings()
    .map((warning) => ({
      label: warningSourceLabel(warning.source),
      message: this.cleanText(warning.message)
    }))
    .filter((warning) => warning.message !== ''))

  readonly siteFraz = computed(() => this.site()?.fraz ?? null)

  readonly detailMetaFields = computed(() => {
    const event = this.detailEvent()
    if (!event?.meta) {
      return []
    }
    return Object.entries(event.meta)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({ key, label: this.humanizeKey(key), value: this.formatDetailValue(value) }))
  })

  readonly detailFields = computed(() => {
    const row = this.detailRow()
    if (!row) {
      return []
    }
    const hidden = new Set(['ClassName', 'CurrentId', 'IdTenant', 'UserDelete'])
    return Object.entries(row)
      .filter(([key, value]) => !hidden.has(key) && value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({ key, label: this.humanizeKey(key), value: this.formatDetailValue(value) }))
  })

  private readonly frazEffect = effect(() => {
    const value = this.frazInput()
    if (value) {
      this.fraz.set(value)
      this.load()
    }
  })

  ngOnInit (): void {
    // In modalita' incorporata la sede arriva da frazInput e la carica l'effect.
    if (this.embedded() || this.frazInput()) {
      return
    }
    this.loadSites()
    const fraz = this.route.snapshot.queryParamMap.get('fraz')
    if (fraz) {
      this.fraz.set(fraz)
      this.load()
    }
  }

  loadSites (): void {
    this.classApi.getAnagraficaSiti().subscribe({
      next: (rows) => {
        const options = (Array.isArray(rows) ? rows : [])
          .filter((row) => row && row.fraz)
          .map((row) => ({
            label: `${row.fraz}${row.denominazione ? ' — ' + row.denominazione : ''}`,
            value: String(row.fraz)
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        this.sites.set(options)
      },
      error: () => { /* il campo resta digitabile */ }
    })
  }

  onSiteChange (fraz: string): void {
    this.fraz.set(fraz || '')
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: fraz ? { fraz } : {},
      queryParamsHandling: 'merge',
      replaceUrl: true
    })
    if (fraz) {
      this.load()
    }
  }

  load (force = false): void {
    const fraz = this.fraz().trim()
    if (!fraz || this.loading()) {
      return
    }
    if (!force && this.loadedFraz === fraz) {
      return
    }
    this.loadedFraz = fraz
    const token = ++this.loadToken
    this.loading.set(true)
    this.error.set(null)
    this.warnings.set([])
    this.visible.set(40)
    this.api.getTimeline({ fraz, limit: 50 }, force)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (token !== this.loadToken) {
            return
          }
          this.site.set(response.site)
          this.events.set(Array.isArray(response.events) ? response.events : [])
          this.warnings.set(Array.isArray(response.warnings) ? response.warnings : [])
          this.loading.set(false)
        },
        error: (err) => {
          if (token !== this.loadToken) {
            return
          }
          this.loadedFraz = ''
          this.loading.set(false)
          const detail = err?.error?.message ?? 'Errore durante il caricamento della timeline'
          this.error.set(String(detail))
          if (!err.isintercepted) {
            this.messageService.add({ severity: 'error', summary: 'Timeline', detail: String(detail) })
          }
        }
      })
  }

  refresh (): void {
    this.load(true)
  }

  setMode (mode: 'all' | 'operational'): void {
    this.mode.set(mode)
    this.visible.set(40)
  }

  setArea (area: string): void {
    this.area.set(area || '')
    // Se l'utente filtra esplicitamente per RTU, il toggle deve seguirlo.
    if (area === 'rtu') {
      this.showRtu.set(true)
    }
    this.visible.set(40)
  }

  /** Toggle degli eventi RTU: spegnendolo si esce dal filtro area RTU. */
  setShowRtu (value: boolean): void {
    this.showRtu.set(!!value)
    if (!value && this.area() === 'rtu') {
      this.area.set('')
    }
    this.visible.set(40)
  }

  loadMore (): void {
    this.visible.update((value) => value + 40)
  }

  areaLabel (area: string): string {
    return this.areas.find((item) => item.value === area)?.label ?? area
  }

  areaIcon (area: string): string {
    switch (area) {
      case 'fortinet':
      case 'cisco':
        return 'fa-solid fa-network-wired'
      case 'rtu':
        return 'fa-solid fa-server'
      case 'speedtest':
        return 'fa-solid fa-gauge-high'
      case 'nrd':
        return 'fa-solid fa-globe'
      case 'voip':
        return 'fa-solid fa-phone'
      case 'workflow':
        return 'fa-solid fa-sitemap'
      case 'pianificazione':
        return 'fa-solid fa-calendar-days'
      case 'support':
        return 'fa-solid fa-circle-info'
      case 'queue':
        return 'fa-solid fa-list-check'
      default:
        return 'fa-solid fa-timeline'
    }
  }

  statusClass (status: SiteTimelineEventStatus): string {
    switch (status) {
      case 'success':
        return 'is-success'
      case 'error':
        return 'is-error'
      case 'warning':
        return 'is-warning'
      default:
        return 'is-info'
    }
  }

  statusLabel (status: SiteTimelineEventStatus): string {
    switch (status) {
      case 'success':
        return 'OK'
      case 'error':
        return 'Errore'
      case 'warning':
        return 'Attenzione'
      default:
        return 'Info'
    }
  }

  formatDay (date: string): string {
    const parsed = new Date(date)
    if (Number.isNaN(parsed.getTime())) {
      return ''
    }
    return parsed.toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
  }

  formatTime (date: string): string {
    const parsed = new Date(date)
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
  }

  /** Testo leggibile: alcune fonti (es. Tunnel_Status) restituiscono HTML. */
  cleanText (value: string | null | undefined): string {
    const text = stripHtml(value)
    return text.replace(/\[object Object\]/gi, '').trim()
  }

  formatDateTime (value: string | null | undefined): string {
    if (!value) {
      return '—'
    }
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime())
      ? '—'
      : parsed.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
  }

  openDetail (event: SiteTimelineEvent): void {
    const token = ++this.detailRequestToken
    this.detailEvent.set(event)
    this.detailVisible.set(true)
    this.detailRow.set(null)
    this.detailTable.set(null)
    this.detailError.set(null)
    this.detailLoading.set(true)

    this.api.getEventDetail(event.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          if (token !== this.detailRequestToken) {
            return
          }
          this.detailRow.set(detail?.row ?? null)
          this.detailTable.set(detail?.table ?? null)
          this.detailLoading.set(false)
        },
        error: () => {
          if (token !== this.detailRequestToken) {
            return
          }
          this.detailLoading.set(false)
          this.detailError.set('Dettaglio non disponibile per questo evento')
        }
      })
  }

  closeDetail (): void {
    this.detailRequestToken += 1
    this.detailVisible.set(false)
    this.detailLoading.set(false)
  }

  private humanizeKey (key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, (char) => char.toUpperCase())
  }

  private formatDetailValue (value: any): string {
    if (value === null || value === undefined || value === '') {
      return '—'
    }
    if (typeof value === 'boolean') {
      return value ? 'Sì' : 'No'
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(this.sanitizeDetailValue(value), null, 2)
      } catch {
        return String(value)
      }
    }
    return stripHtml(String(value))
  }

  /** Evita di mostrare HTML crudo dentro i campi JSON (es. Tunnel_Status.htmlResult). */
  private sanitizeDetailValue (value: any): any {
    if (typeof value === 'string') {
      return stripHtml(value)
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.sanitizeDetailValue(item))
    }
    if (value && typeof value === 'object') {
      return Object.entries(value).reduce((result, [key, item]) => {
        result[key] = this.sanitizeDetailValue(item)
        return result
      }, {} as Record<string, any>)
    }
    return value
  }

  trackEvent (_index: number, event: SiteTimelineEvent): string {
    return event.id
  }
}
